import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { countries, getCountryByCode } from '../data/countries';
import { getGeoLocation, submitLead, getDialCodeFromCountry, navigateToApplication } from '../services/api';

const ContactPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    service: '',
    message: ''
  });

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const result = await getGeoLocation();
      if (result.success && result.data) {
        setGeoData(result.data);
        
        // Set default country code based on geo location
        if (result.data.country) {
          const dialCode = getDialCodeFromCountry(result.data.country);
          setFormData(prev => ({ ...prev, countryCode: `+${dialCode}` }));
        }
      }
    };
    
    fetchGeoLocation();
  }, []);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    const currentCountry = getCountryByCode(formData.countryCode);
    
    // Limit to max length for selected country
    if (value.length <= currentCountry.maxLength) {
      setFormData({ ...formData, phone: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get country code from dial code
      const dialCode = parseInt(formData.countryCode.replace('+', ''));
      const country = geoData?.country || 'US';
      const ipAddress = geoData?.ipAddress || '0.0.0.0';

      // Build description with service type if provided
      const description = formData.service 
        ? `Service: ${formData.service}\n\n${formData.message}`
        : formData.message;

      // Submit lead using centralized API service
      const result = await submitLead({
        name: formData.name,
        email: formData.email,
        countryDialCode: dialCode,
        country: country,
        phoneNumber: formData.phone || '0000000000', // Provide default if no phone
        ipAddress: ipAddress,
        description: description,
        pageUrl: window.location.href,
        geoLocationData: geoData // Pass full geolocation data
      });

      if (result.success) {
        toast({
          title: 'Success!',
          description: result.message || 'Your message has been sent successfully.',
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryCode: formData.countryCode,
          service: '',
          message: ''
        });

        // Redirect to application page
        setTimeout(() => {
          navigateToApplication(result.customerId);
        }, 1500);

      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive'
        });
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Contact Us - Get Expert Trademark Assistance | Global Trademark USA</title>
        <meta name="description" content="Contact Global Trademark USA for expert trademark registration assistance. Get answers to your questions and start protecting your brand today." />
        <meta name="keywords" content="contact trademark attorney, trademark help, trademark consultation, brand protection contact" />
        <link rel="canonical" href="https://yourdomain.com/contact" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
              Contact Us
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Have questions about trademark registration? Our team is here to help. Reach out and let's protect your brand together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {/* Contact Form */}
            <div className="bg-gradient-to-br from-gray-50 to-emerald-50 rounded-2xl p-8 shadow-xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xl font-semibold text-gray-700 mb-2">Full Name *</label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xl font-semibold text-gray-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xl font-semibold text-gray-700 mb-2">Phone Number</label>
                  <div className="flex gap-2">
                    <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value, phone: '' })}>
                      <SelectTrigger className="w-28 h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {countries.map((country, index) => (
                          <SelectItem key={index} value={country.code}>
                            {country.flag} {country.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="flex-1 h-11"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xl font-semibold text-gray-700 mb-2">Service Interest</label>
                  <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trademark">Trademark Registration</SelectItem>
                      <SelectItem value="copyright">Copyright Registration</SelectItem>
                      <SelectItem value="company">Company Formation</SelectItem>
                      <SelectItem value="amazon">Amazon Brand Registry</SelectItem>
                      <SelectItem value="search">Trademark Search</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xl font-semibold text-gray-700 mb-2">Message *</label>
                  <Textarea
                    placeholder="Tell us about your trademark needs..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="min-h-[150px]"
                    required
                  />
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full h-11 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  We're here to answer your questions and help you protect your brand. Reach out through any of these channels.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4 bg-gradient-to-br from-emerald-50 to-blue-50 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">info@globaltrademarkusa.com</p>
                    <p className="text-gray-600">support@globaltrademarkusa.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gradient-to-br from-blue-50 to-emerald-50 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600">+1 (888) 909 9199</p>
                    <p className="text-xl text-gray-500">Mon-Fri: 9AM - 6PM EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gradient-to-br from-emerald-50 to-blue-50 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Office</h3>
                    <p className="text-gray-600">714 E Carson St</p>
                    <p className="text-gray-600">Carson, CA 90745, United States</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gradient-to-br from-blue-50 to-emerald-50 p-6 rounded-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Business Hours</h3>
                    <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                    <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM EST</p>
                    <p className="text-gray-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Have Questions Before You Reach Out?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Check out our FAQ section for quick answers to common questions about trademark registration.
            </p>
            <a href="/#faq">
              <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800">
                View FAQs
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;